import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import { useGameState } from "../providers/GameStateProvider";

interface GoalPopupProps {
  onClose: () => void;
  levelNickname?: string;
}

const GoalPopup: React.FC<GoalPopupProps> = ({ onClose, levelNickname }) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Allow time for fade out animation
  };

  if (!isVisible) {
    return null;
  }

  // Get the appropriate message based on level
  const getGoalMessage = () => {
    switch (levelNickname) {
      case "yic-kane":
        return {
          title: "🎯 Goal",
          mainText: "Meet the 50/30/20 rule in as few moves as possible",
          details: [
            "• 50% for Needs (necessary expenses)",
            "• 30% for Wants (discretionary spending)", 
            "• 20% for Savings"
          ]
        };
      case "yic-liz":
        return {
          title: "🎯 Goal",
          mainText: "Liz wants to save $5,000 for a new car in 6 months. Help her figure out how much to set aside each month to meet her goal.",
          details: []
        };
      default:
        return {
          title: "🎯 Goal",
          mainText: "Meet the 50/30/20 rule in as few moves as possible",
          details: [
            "• 50% for Needs (necessary expenses)",
            "• 30% for Wants (discretionary spending)",
            "• 20% for Savings"
          ]
        };
    }
  };

  const goalMessage = getGoalMessage();

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        animation: "fadeIn 0.3s ease-in-out",
        "@keyframes fadeIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 4,
          maxWidth: 400,
          mx: 2,
          textAlign: "center",
          backgroundColor: "#2d3748",
          color: "white",
          borderRadius: 3,
          animation: "slideIn 0.3s ease-out",
          "@keyframes slideIn": {
            from: { transform: "translateY(-50px)", opacity: 0 },
            to: { transform: "translateY(0)", opacity: 1 },
          },
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          sx={{
            mb: 2,
            fontWeight: "bold",
            color: "#fbbf24",
            textShadow: "0 2px 4px rgba(0,0,0,0.3)",
          }}
        >
          {goalMessage.title}
        </Typography>
        
        <Typography
          variant="body1"
          sx={{
            mb: 3,
            fontSize: "16px",
            lineHeight: 1.6,
            color: "#e2e8f0",
          }}
        >
          {goalMessage.mainText}
        </Typography>

        {goalMessage.details.length > 0 && (
          <Typography
            variant="body2"
            sx={{
              mb: 3,
              fontSize: "14px",
              color: "#a0aec0",
              fontStyle: "italic",
            }}
          >
            {goalMessage.details.map((detail, index) => (
              <React.Fragment key={index}>
                {detail}<br/>
              </React.Fragment>
            ))}
          </Typography>
        )}

        <Button
          variant="contained"
          onClick={handleClose}
          sx={{
            backgroundColor: "#fbbf24",
            color: "#1a202c",
            fontWeight: "bold",
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "16px",
            "&:hover": {
              backgroundColor: "#f59e0b",
            },
          }}
        >
          Got it!
        </Button>
      </Paper>
    </Box>
  );
};

export default GoalPopup;
